import Link from 'next/link'
import { NextSeo } from 'next-seo'
import { Header } from '@components/Header'
import { Footer } from '@components/Footer'
import { Button } from '@components/UI/Button'
import { ArrowRightIcon, ClockIcon } from '@heroicons/react/solid'
import { HandshakeIcon } from '@components/UI/Icons/handshake'
import { BullseyeIcon } from '@components/UI/Icons/bullseye'
import { ChartLineIcon } from '@components/UI/Icons/chartLine'

export default function PropertyInvestors() {
  return (
    <>
      <NextSeo
        title="Property Investors"
        description="Transform your acquisition strategy after the home inspection with Majordomo’s repair estimates. Delivered within 24 hours to complete deals faster."
        canonical="https://majordomo.com/solutions/for-property-investors"
        openGraph={{
          url: 'https://majordomo.com/solutions/for-property-investors',
          title: 'Property Investors | Majordomo',
          description: 'Transform your acquisition strategy after the home inspection with Majordomo’s repair estimates. Delivered within 24 hours to complete deals faster.',
          images: [
            {
              url: 'https://majordomo.com/images/product-pages/investors-hero.png',
              width: 700,
              height: 681,
              alt: 'A row of five red Monopoly hotel game pieces'
            },
            {
              url: 'https://majordomo.com/images/product-pages/domoreport.png',
              width: 1816,
              height: 1992,
              alt: 'Screen shot of Majordomo’s Domoreport'
            }
          ]
        }}
      />
      <Header />

      <main className="pt-8 px-0 container mx-auto">
        <div className="flex md:flex-row flex-col justify-between items-center lg:pb-36 pb-12">
          <div className="flex flex-col md:items-start items-center justify-center w-full 2xl:p-36 xl:p-20 md:p-8 md:order-first order-last">
            <div className="flex items-center border-b border-gray-200 mb-4 pb-4 md:w-full">
              <ChartLineIcon className="pr-2 mr-2 border-r border-gray-200 w-8 h-8 fill-current text-brand" />
              <h4 className="text-2xl font-black tracking-wide text-brand uppercase">
                Property Investors
              </h4>
            </div>

            <p className="mb-8 xl:text-3xl lg:text-2xl text-xl font-light">
              <span className="font-black">Faster</span> deals
              <br />& <span className="font-black">Higher</span> cash flow
            </p>

            <p className="md:w-full md:p-0 px-8">
              Integrate Majordomo into your property acquisition system so you
              can <span className="font-black">let Majordomo work for you</span>
              . You're then free to focus on more high-touch and profitable
              activities that offer better use of your time, energy, skills and
              expertise – all while you{' '}
              <span className="font-black">generate higher cash flow</span>.
            </p>
          </div>

          <img
            src="/images/product-pages/investors-hero.png"
            className="md:order-last order-first md:mb-0 mb-8"
            width="50%"
          />
        </div>

        <div className="lg:mb-36 sm:mb-12 mb-0">
          <h4 className="text-center md:text-2xl text-lg font-black tracking-tight md:px-0 px-8 lg:pb-0 pb-8 max-w-md mx-auto">
            Transform your acquisition strategy after the inspection
          </h4>

          <div className="flex lg:flex-row flex-col justify-evenly lg:px-16 px-4">
            <div className="space-y-12 md:p-16 p-4 lg:w-1/2 w-full flex flex-col items-center lg:order-first order-last">
              <div className="flex relative">
                <ClockIcon
                  width={56}
                  className="absolute top-0 md:-left-24 md:w-16 w-12 md:h-16 h-12 text-white bg-gold-400 p-2"
                />

                <div>
                  <h4 className="max-w-lg mr-auto md:text-2xl text-lg font-black tracking-tight mb-2 md:mt-0 mt-16">
                    Save time after the inspection
                  </h4>
                  <p className="max-w-lg mr-auto">
                    Our guaranteed 24-hour turnaround allows you to focus sooner
                    on the cash-flow analysis and start negotiations sooner.{' '}
                    <span className="font-black">
                      No more running around or waiting for contractors to
                      return calls
                    </span>
                    . Also available is our simple CSV download so you can
                    intregate Majordomo directly into your existing systems.{' '}
                    <a
                      href="https://intercom.help/majordomo/en/articles/4220719-how-do-i-download-the-domoreport-s-data"
                      className="text-accentBlue hover:underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Learn more
                    </a>
                  </p>
                </div>
              </div>

              <div className="flex relative">
                <BullseyeIcon className="absolute top-0 md:-left-24 md:w-16 w-12 md:h-16 h-12 fill-current text-white bg-gold-400 p-2" />

                <div>
                  <h4 className="max-w-lg mr-auto md:text-2xl text-lg font-black tracking-tight mb-2 md:mt-0 mt-16">
                    Precise local estimates, reviewed by humans
                  </h4>
                  <p className="max-w-lg mr-auto">
                    Majordomo's repair-estimate data comes from analysts who
                    study contractor and insurance invoices for 20,000 projects
                    from 35,000 providers for material, equipment, labor and
                    installation costs across the US. Every Domoreport is
                    reviewed by an industry expert and will give you a head
                    start on estimating your underwriting repair costs.{' '}
                    <a
                      href="https://blog.majordomo.com/majordomos-repair-cost-estimates-are-local-and-accurate/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-accentBlue hover:underline"
                    >
                      Learn more
                    </a>
                  </p>
                </div>
              </div>

              <div className="flex relative">
                <HandshakeIcon className="absolute top-0 md:-left-24 md:w-16 w-12 md:h-16 h-12 fill-current text-white bg-gold-400 p-2" />

                <div>
                  <h4 className="max-w-lg mr-auto md:text-2xl text-lg font-black tracking-tight mb-2 md:mt-0 mt-16">
                    Negotiate smarter with sellers
                  </h4>
                  <p className="max-w-lg mr-auto">
                    The Domoreport's ZIP Code specific repair estimates are
                    organized into a simple and clear report that outlines
                    project details and cost estimates, so negotiations can be
                    based on facts, not guesses. Use our "Request List" tool to
                    build a customized request list to send to the sellers;{' '}
                    <span className="font-black">
                      no more cut-and-paste addendum projects
                    </span>
                    . Save your custom request list addendum PDF for a
                    professional edge when negotiating for seller concessions.{' '}
                    <a
                      href="https://intercom.help/majordomo/en/articles/4641002-how-do-i-create-and-use-the-request-list"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-accentBlue hover:underline"
                    >
                      Learn more
                    </a>
                  </p>
                </div>
              </div>

              <div className="flex relative self-start 2xl:ml-8 lg:ml-0 md:ml-12 sm:ml-8 ml-0">
                <h4 className="max-w-lg md:text-2xl text-lg font-black tracking-tight mb-2">
                  All for just $99
                  <span className="font-light text-base"> / per report</span>
                </h4>
              </div>
            </div>

            <div className="flex items-center justify-center lg:w-1/2 w-full lg:p-16 lg:order-last order-first">
              <img
                src="/images/product-pages/domoreport.png"
                alt="Majordomo product samples"
                className="xl:max-w-xl lg:max-w-md max-w-xs"
              />
            </div>
          </div>

          <div className="flex md:flex-row flex-col justify-evenly items-center w-full md:space-y-0 space-y-4 md:pb-12 pb-0 md:pt-0 pt-8">
            <Link href="/checkout">
              <Button variant="primary" clipSide="right">
                Get repair estimates{' '}
                <ArrowRightIcon width={18} height={18} className="ml-2" />
              </Button>
            </Link>

            <Link href="/features/domoreport">
              <a className="flex items-center justify-center font-bold text-accentBlue hover:underline">
                Learn More{' '}
                <ArrowRightIcon width={18} height={18} className="ml-2" />
              </a>
            </Link>
          </div>

          <div className="flex md:flex-row flex-col justify-evenly items-center w-full md:space-y-0 space-y-4 md:pb-12 pb-0 md:pt-0 pt-8">
            <p>
              Interested in bulk purchases?{' '}
              <Link href="/contact-us">
                <a className="text-accentBlue hover:underline">Contact us</a>
              </Link>
            </p>
          </div>
        </div>

        <div className="flex lg:flex-row flex-col justify-evenly lg:mb-36 mb-12">
          <div className="bg-black lg:mt-32 mt-0 lg:w-1/2 md:p-16 p-8 text-gray-300 space-y-8">
            <h4 className="md:text-3xl text-xl font-black tracking-tight text-white">
              Majordomo Is On Your Team
            </h4>

            <div>
              <h4 className="md:text-2xl text-lg font-black tracking-tight mb-2 text-white">
                Additional resources
              </h4>

              <p>
                <b>Explore:</b> We offer a full page of online resources,
                specific to your local area, to help buyers. The information
                includes homeowner rebates, permits, maps, utilities and much
                more.{' '}
                <Link href="/features/explore">
                  <a className="text-accentBlue hover:underline">Learn more</a>
                </Link>
              </p>
            </div>

            <p>
              <b>Documents:</b> Save your home inspection report, photos and
              other important documents to Majordomo Documents and access them
              from any web browser; this service is free forever with every
              Domoreport.{' '}
              <Link href="/features/documents">
                <a className="text-accentBlue hover:underline">Learn more</a>
              </Link>
            </p>

            <p>
              <b>Integrated Request List:</b> Build a custom request list of
              repair requests, credit amounts, and items accepted as-is. Export
              a custom addendum.{' '}
              <a
                href="https://intercom.help/majordomo/en/articles/4641002-how-do-i-create-and-use-the-request-list"
                className="text-accentBlue hover:underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                Learn more
              </a>
            </p>

            <div>
              <h4 className="md:text-2xl text-lg font-black tracking-tight mb-2 text-white">
                Easy to share
              </h4>

              <p>
                Built-in tools easily allow you to share the Domoreport and
                collaborate during the inspection review period.{' '}
                <a
                  href="https://intercom.help/majordomo/en/articles/3216497-how-do-i-share-my-domoreport"
                  className="text-accentBlue hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Learn more
                </a>
              </p>
            </div>

            <div>
              <h4 className="md:text-2xl text-lg font-black tracking-tight mb-2 text-white">
                Always available
              </h4>

              <p>
                Access Majordomo on your computer, smartphone or tablet anytime.
                Have a question? Majordomo is ready to help with 24-hour chat
                and customer support.
              </p>
            </div>
          </div>

          <div className="lg:flex hidden justify-center items-center folded-l-max p-4 lg:w-1/2 w-full bg-gray-50">
            <img
              src="/images/product-pages/on-your-team.png"
              alt="A Majordomo"
              className="object-contain max-w-md"
            />
          </div>
        </div>
      </main>

      <Footer />
    </>
  )
}
